<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import type { HTMLAttributes, Component } from 'vue'
import { cn } from '@/lib/utils'

interface Props {
  class?: HTMLAttributes['class']
  /** Icon component */
  icon?: Component
  /** Item title */
  title?: string
  /** Item subtitle */
  subtitle?: string
  /** Variant for different styling */
  variant?: 'default' | 'spotify' | 'music' | 'display'
  /** Whether to show loop indicator */
  loop?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'default',
  loop: false
})

defineSlots<{
  icon(): any
  default(): any
  meta(): any
  actions(): any
}>()

const emit = defineEmits<{
  remove: []
}>()

// Unique ID for SVG defs
const uniqueId = computed(() => `bubble-item-${Math.random().toString(36).slice(2, 9)}`)

// Container ref for measuring
const containerRef = ref<HTMLDivElement | null>(null)
const measuredWidth = ref(200)
const measuredHeight = ref(60)

let resizeObserver: ResizeObserver | null = null

onMounted(() => {
  if (containerRef.value) {
    measuredWidth.value = containerRef.value.offsetWidth
    measuredHeight.value = containerRef.value.offsetHeight
    
    resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        measuredWidth.value = entry.contentRect.width
        measuredHeight.value = entry.contentRect.height
      }
    })
    resizeObserver.observe(containerRef.value)
  }
})

onUnmounted(() => {
  resizeObserver?.disconnect()
})

// Design constants
const chamferRatio = 0.06
const minChamfer = 4
const maxChamfer = 10
const strokeWidth = 1.5

const chamfer = computed(() => {
  const minDim = Math.min(measuredWidth.value, measuredHeight.value)
  return Math.max(minChamfer, Math.min(maxChamfer, minDim * chamferRatio))
})

const framePath = computed(() => {
  const w = measuredWidth.value
  const h = measuredHeight.value
  const c = chamfer.value
  
  return `
    M ${c} 0
    L ${w - c} 0
    L ${w} ${c}
    L ${w} ${h - c}
    L ${w - c} ${h}
    L ${c} ${h}
    L 0 ${h - c}
    L 0 ${c}
    Z
  `
})

// Gold gradient colors - using CSS custom properties
const gradientColors = computed(() => ({
  start: 'var(--gold-start)',
  mid: 'var(--gold-mid)',
  end: 'var(--gold-end)'
}))

const variantIconClasses = {
  default: 'bg-primary/20 text-primary',
  spotify: 'bg-green-500/20 text-green-500',
  music: 'bg-primary/20 text-primary',
  display: 'bg-blue-500/20 text-blue-500'
}
</script>

<template>
  <div
    ref="containerRef"
    :class="cn('bubble-item relative', props.class)"
  >
    <!-- SVG Frame -->
    <svg
      class="bubble-item-frame"
      :viewBox="`0 0 ${measuredWidth} ${measuredHeight}`"
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient :id="`${uniqueId}-gold`" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" :stop-color="gradientColors.start" />
          <stop offset="50%" :stop-color="gradientColors.mid" />
          <stop offset="100%" :stop-color="gradientColors.end" />
        </linearGradient>
        <filter :id="`${uniqueId}-glow`" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="1" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      
      <!-- Background -->
      <path
        :d="framePath"
        fill="hsl(var(--primary) / 0.08)"
      />
      
      <!-- Border -->
      <path
        :d="framePath"
        fill="none"
        :stroke="`url(#${uniqueId}-gold)`"
        :stroke-width="strokeWidth"
        stroke-linecap="square"
        stroke-linejoin="miter"
        :filter="`url(#${uniqueId}-glow)`"
        class="bubble-item-border"
      />
    </svg>
    
    <!-- Corner accents -->
    <div class="bubble-corner bubble-corner-tl"></div>
    <div class="bubble-corner bubble-corner-tr"></div>
    <div class="bubble-corner bubble-corner-bl"></div>
    <div class="bubble-corner bubble-corner-br"></div>
    
    <!-- Content -->
    <div class="bubble-item-content">
      <!-- Icon slot or default icon -->
      <div :class="cn('h-10 w-10 flex items-center justify-center flex-shrink-0', variantIconClasses[variant])">
        <slot name="icon">
          <component v-if="icon" :is="icon" class="h-5 w-5" />
        </slot>
      </div>
      
      <!-- Text content -->
      <div class="flex-1 min-w-0">
        <div class="text-sm font-medium truncate">
          {{ title }}
        </div>
        <div v-if="subtitle" class="text-xs text-muted-foreground truncate">
          {{ subtitle }}
        </div>
        <slot name="meta" />
      </div>
      
      <!-- Actions -->
      <div class="flex items-center gap-1 flex-shrink-0">
        <slot name="actions" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.bubble-item {
  min-height: 48px;
}

.bubble-item-frame {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: visible;
}

.bubble-item-border {
  vector-effect: non-scaling-stroke;
}

.bubble-item-content {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
}

/* Corner accents */
.bubble-corner {
  position: absolute;
  width: 8px;
  height: 8px;
  pointer-events: none;
  border-color: var(--gold-solid);
  opacity: 0.6;
}

.bubble-corner-tl {
  top: 6px;
  left: 6px;
  border-top: 1px solid;
  border-left: 1px solid;
}

.bubble-corner-tr {
  top: 6px;
  right: 6px;
  border-top: 1px solid;
  border-right: 1px solid;
}

.bubble-corner-bl {
  bottom: 6px;
  left: 6px;
  border-bottom: 1px solid;
  border-left: 1px solid;
}

.bubble-corner-br {
  bottom: 6px;
  right: 6px;
  border-bottom: 1px solid;
  border-right: 1px solid;
}
</style>
